import React from 'react';
import { EventProps } from 'react-big-calendar';
import { Shift } from 'domain/Shift';
import { Text, Button, ButtonVariant, List } from '@patternfly/react-core';
import moment from 'moment';
import { Employee } from 'domain/Employee';
import { convertHardMediumSoftScoreToString } from 'domain/HardMediumSoftScore';
import {
  BlueprintIcon, EditIcon, TrashIcon, ThumbTackIcon, FileContractIcon, SchoolIcon, CalendarTimesIcon,
  UserFriendsIcon, ExclamationTriangleIcon, UserTimesIcon, UserMinusIcon, UserPlusIcon, AlignCenterIcon,
  PhoneVolumeIcon,
} from '@patternfly/react-icons';
import Color from 'color';
import { useTranslation } from 'react-i18next';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'ui/components/calendar/ReactBigCalendarOverrides.css';

export function Indictments(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  const indictmentList = (
    <List>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <PublishedShiftReassignedPenalties {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <RequiredSkillViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ContractMinutesViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <UnavailableEmployeeViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ShiftEmployeeConflictViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <UnassignedShiftPenalties {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <RotationViolationPenalties {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <UndesiredTimeslotForEmployeePenalties {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <DesiredTimeslotForEmployeeRewards {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <NoBreaksViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BalanceEmployeeViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <RestOneDayViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <DifferentEmployeeViolations {...shift} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BloquedViolationPenalties {...shift} />
    </List>
  );

  if (React.Children.count(indictmentList) === 0) {
    return null;
  }
  return (
    <>
      <Text>{t('indictments')}</Text>
      {indictmentList}
    </>
  );
}

export function IndictmentIcons(shift: Shift) {
  const isPresent = (list?: any[]) => (list && list.length) || null;
  return (
    <div>
      {isPresent(shift.publishedShiftReassignedPenaltyList) && <PhoneVolumeIcon />}
      {isPresent(shift.contractMinutesViolationPenaltyList) && <FileContractIcon />}
      {isPresent(shift.desiredTimeslotForEmployeeRewardList) && <UserPlusIcon />}
      {isPresent(shift.requiredSkillViolationList) && <SchoolIcon />}
      {isPresent(shift.rotationViolationPenaltyList) && <CalendarTimesIcon />}
      {isPresent(shift.shiftEmployeeConflictList) && <UserFriendsIcon />}
      {isPresent(shift.unassignedShiftPenaltyList) && <ExclamationTriangleIcon />}
      {isPresent(shift.unavailableEmployeeViolationList) && <UserTimesIcon />}
      {isPresent(shift.undesiredTimeslotForEmployeePenaltyList) && <UserMinusIcon />}
      {isPresent(shift.noBreakViolationList) && <AlignCenterIcon />}
      {isPresent(shift.balanceEmployeeViolationList) && <CalendarTimesIcon />}
      {isPresent(shift.restOneDayViolationList) && <CalendarTimesIcon />}
      {isPresent(shift.differentEmployeeDayShiftsViolationList) && <CalendarTimesIcon />}
      {isPresent(shift.bloquedViolationPenaltyList) && <UserFriendsIcon />}
    </div>
  );
}

export function PublishedShiftReassignedPenalties(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.publishedShiftReassignedPenaltyList || []).map((v) => (
        <li key={v.shift.id}>
          {t('publishedShiftReassigned', {
            employee: (v.shift.employee as Employee).name,
            originalEmployee: (v.shift.originalEmployee as Employee).name,
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function RequiredSkillViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.requiredSkillViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t(
            'missingRequiredSkills',
            {
              employee: (v.shift.employee as Employee).name,
              spot: v.shift.spot.name,
            },
          )}
          <List>
            {v.shift.spot.requiredSkillSet.filter((skill) => (v.shift.employee as Employee).skillProficiencySet
              .find((s) => s.id === skill.id) === undefined)
              .map((skill) => <li key={skill.id}>{skill.name}</li>)}
          </List>
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function BalanceEmployeeViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.balanceEmployeeViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t(
            'noBalanceEmployee',
            {
              employee: (v.shift.employee as Employee).name,
            },
          )}
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function DifferentEmployeeViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.differentEmployeeDayShiftsViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t(
            'differentEmployeeShift',
            {
              spot: v.shift.spot.name,
              date: moment(v.shift.startDateTime).format('LLL'),
            },
          )}
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function RestOneDayViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.restOneDayViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t(
            'norestinweek',
            {
              employee: (v.shift.employee as Employee).name,
            },
          )}
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function ContractMinutesViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.contractMinutesViolationPenaltyList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('exceededContractMaximumMinutes', {
            employee: (v.employee as Employee).name,
            // eslint-disable-next-line no-nested-ternary
            durationly: t((v.type === 'DAY' ? 'daily'
              // eslint-disable-next-line no-nested-ternary
              : v.type === 'WEEK' ? 'weekly'
                : v.type === 'MONTH' ? 'monthly'
                  : 'yearly')),
            duration: t(v.type.toLowerCase()),
            minutesWorked: v.minutesWorked,
            // eslint-disable-next-line no-nested-ternary
            maxMinutesAllowed: (v.type === 'DAY' ? v.employee.contract.maximumMinutesPerDay
              // eslint-disable-next-line no-nested-ternary
              : v.type === 'WEEK' ? v.employee.contract.maximumMinutesPerWeek
                : v.type === 'MONTH' ? v.employee.contract.maximumMinutesPerMonth
                  : v.employee.contract.maximumMinutesPerYear),
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function UnavailableEmployeeViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.unavailableEmployeeViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('unavailableEmployee', {
            employee: v.employeeAvailability.employee.name,
            from: moment(v.employeeAvailability.startDateTime).format('LLL'),
            to: moment(v.employeeAvailability.endDateTime).format('LLL'),
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

// NOTE: ShiftEmployeeConflict refer to indictments from two constraints:
// - "No overlapping shifts"
// - "Break between non-consecutive shifts is at least 10 hours"
export function ShiftEmployeeConflictViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.shiftEmployeeConflictList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('conflictingShifts', {
            employee: (v.leftShift.employee as Employee).name,
            spot: (v.leftShift.id === shift.id) ? v.rightShift.spot.name : v.leftShift.spot.name,
            from: (v.leftShift.id === shift.id) ? moment(v.rightShift.startDateTime).format('LT')
              : moment(v.leftShift.startDateTime).format('LT'),
            to: (v.leftShift.id === shift.id) ? moment(v.rightShift.endDateTime).format('LT')
              : moment(v.leftShift.endDateTime).format('LT'),
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function NoBreaksViolations(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.noBreakViolationList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('noBreaks', {
            employee: (v.firstShift.employee as Employee).name,
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function RotationViolationPenalties(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');

  return (
    <>
      {(shift.rotationViolationPenaltyList || []).map((v) => (
        <li key={v.shift.id}>
          {t('employeeDoesNotMatchRotationEmployee', {
            employee: v.shift.employee?.name ?? 'Sin asignar',
            rotationEmployee: v.shift.rotationEmployee?.name ?? 'Sin definir',
          })}
          <br />
          {t('penalty', {
            score: convertHardMediumSoftScoreToString(v.score),
          })}
        </li>
      ))}
    </>
  );
}

export function UnassignedShiftPenalties(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.unassignedShiftPenaltyList || []).map((v) => (
        <li key={v.shift.id}>
          {t('unassignedShift')}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function UndesiredTimeslotForEmployeePenalties(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.undesiredTimeslotForEmployeePenaltyList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('undesiredShiftForEmployee', {
            employee: v.employeeAvailability.employee.name,
            from: moment(v.employeeAvailability.startDateTime).format('LLL'),
            to: moment(v.employeeAvailability.startDateTime).format('LLL'),
          })}
          <br />
          {t('penalty', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function DesiredTimeslotForEmployeeRewards(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <>
      {(shift.desiredTimeslotForEmployeeRewardList || []).map((v, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={String(index)}>
          {t('desiredShiftForEmployee', {
            employee: v.employeeAvailability.employee.name,
            from: moment(v.employeeAvailability.startDateTime).format('LLL'),
            to: moment(v.employeeAvailability.startDateTime).format('LLL'),
          })}
          <br />
          {t('reward', { score: convertHardMediumSoftScoreToString(v.score) })}
        </li>
      ))}
    </>
  );
}

export function BloquedViolationPenalties(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');

  return (
    <>
      {(shift.bloquedViolationPenaltyList || []).map((v, index) => (
        <li key={String(index)}>
          {t('employeeBlockedInSpot', {
            employee: v.shift.employee?.name ?? 'Sin asignar',
            spot: v.shift.spot?.name ?? 'Sin definir',
          })}
          <br />
          {t('penalty', {
            score: convertHardMediumSoftScoreToString(v.score),
          })}
        </li>
      ))}
    </>
  );
}

export const NEGATIVE_HARD_SCORE_COLOR = Color('#cc0000');
export const NEGATIVE_MEDIUM_SCORE_COLOR = Color('#fce94f');
export const NEGATIVE_SOFT_SCORE_COLOR = Color('#fcaf3e');
export const ZERO_SCORE_COLOR = Color('#eeeeec');
export const POSITIVE_SOFT_SCORE_COLOR = Color('#73d216');

export function getShiftColor(shift: Shift): string {
  if (shift.indictmentScore !== undefined && shift.indictmentScore.hardScore < 0) {
    return NEGATIVE_HARD_SCORE_COLOR.hex();
  }
  if (shift.indictmentScore !== undefined && shift.indictmentScore.mediumScore < 0) {
    return NEGATIVE_MEDIUM_SCORE_COLOR.hex();
  }
  if (shift.indictmentScore !== undefined && shift.indictmentScore.softScore < 0) {
    return NEGATIVE_SOFT_SCORE_COLOR.hex();
  }
  if (shift.indictmentScore !== undefined && shift.indictmentScore.softScore > 0) {
    return POSITIVE_SOFT_SCORE_COLOR.hex();
  }

  // Zero score
  return ZERO_SCORE_COLOR.hex();
}

function ShiftPopupHeader({
  shift,
  onEdit,
  onCopy,
  onDelete,
}: {
  shift: Shift;
  // eslint-disable-next-line no-shadow, no-unused-vars
  onEdit: (shift: Shift) => void;
  // eslint-disable-next-line no-shadow, no-unused-vars
  onCopy?: (shift: Shift) => void;
  // eslint-disable-next-line no-shadow, no-unused-vars
  onDelete: (shift: Shift) => void;
}) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <span>
      <Text>
        {
          `${shift.spot.name}, ${
            moment(shift.startDateTime).format('LT')}-${
            moment(shift.endDateTime).format('LT')}: ${
            shift.employee ? shift.employee.name : t('unassigned')}`
        }
      </Text>
        { shift.originalEmployee === null ? 
          <Button
              onClick={() => onEdit(shift)}
            variant={ButtonVariant.link}
          >
            <EditIcon />
          </Button> : ''
        }
       
      {
        onCopy && shift.originalEmployee === null
          ? (
            <Button
              onClick={() => onCopy(shift)}
              variant={ButtonVariant.link}
            >
              <BlueprintIcon />
            </Button>
          )
          : null
      }
      { shift.originalEmployee === null ? 
      <Button
        onClick={() => onDelete(shift)}
        variant={ButtonVariant.link}
      >
        <TrashIcon />
      </Button> : ''
        }
    </span>
  );
}

function ShiftPopupBody(shift: Shift) {
  const { t } = useTranslation('ShiftEvent');
  return (
    <span
      style={{
        display: 'block',
        maxHeight: '20vh',
        overflowY: 'auto',
        pointerEvents: 'all',
      }}
    >

      {t('totalIndictment', { score: shift.indictmentScore ? convertHardMediumSoftScoreToString(shift.indictmentScore)
        : 'N/A' })}
      <br />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Indictments {...shift} />
    </span>
  );
}

function ShiftEvent(props: EventProps<Shift>) {
  return (
    <span
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'calc(100% - 60px) 55px',
        gridRowGap: '5px',
        height: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {props.event.pinnedByUser && <ThumbTackIcon />}
        <span style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
        }}
        >
          {`${props.title} ${props.event.requiredSkillSet.length
            ? `(${props.event.requiredSkillSet.map((s) => s.name).join(', ')})` : ''} ${
            (props.event.originalEmployee && props.event.employee
              && props.event.originalEmployee.id !== props.event.employee.id)
              ? `(was ${props.event.originalEmployee.name})` : ''}`}
        </span>
      </div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <IndictmentIcons {...props.event} />
    </span>
  );
}

ShiftPopupHeader.defaultProps = {
  onCopy: undefined,
};

export { ShiftPopupBody, ShiftPopupHeader };

export default ShiftEvent;
